import { withRouter as withReactRouter, Route as ReactRouterRoute } from 'react-router'
import { Link as ReactRouterLink, NavLink } from 'react-router-dom'
import { withProps, branch, wrapDisplayName } from 'recompose'
import { stringify, parse } from 'qs'
import moment from 'moment'
import { Helmet } from 'react-helmet-async'
import { injectIntl, FormattedMessage, useIntl } from 'react-intl'

import { languageSelector, localeSelector, pathLanguageRegexp } from './selectors'

export const getTimeFormat = () => moment.localeData().longDateFormat('LT')
export const getDateFormat = () => moment.localeData().longDateFormat('L')
export const getDateTimeFormat = () => {
    const locale = moment.localeData()
    return `${locale.longDateFormat('L')} ${locale.longDateFormat('LT')}`
}

export const withLanguage = connect(
    createStructuredSelector({
        language: languageSelector,
    })
)

const prependWithSlash = string =>
    string === '/' || string === '' ? '' : string.startsWith('/') ? string : `/${string}`
export const stripLanguage = path => (path.match(pathLanguageRegexp) ? path.substr(3) : path)
export const createPath = (language, path, params) =>
    `/${language}${prependWithSlash(path)}${params ? `?${stringify(params)}` : ''}`

export const withRouter = compose(
    withReactRouter,
    withLanguage,
    withProps(({ language, history, location }) => ({
        history: {
            ...history,
            push: (path, state, params) => history.push(createPath(language, path, params), state),
            replace: (path, state, params) => history.replace(createPath(language, path, params), state),
        },
        location: {
            ...location,
            pathname: stripLanguage(location.pathname),
            query: parse(location.search.substr(1)),
        },
    }))
)

export const Route = ({ path = '', language, ...props }) => (
    <ReactRouterRoute {...props} path={`/${language || ':language'}${prependWithSlash(path)}`} />
)

Route.propTypes = {
    path: PropTypes.string,
}

const $Link = ({ navLink, language, to, dispatch, ...props }) => {
    const toIsObject = typeof to === 'object'

    const providedPath = toIsObject ? to.pathname : to
    const providedSearch =
        toIsObject &&
        (typeof to.search === 'object' ? stringify(to.search) : typeof to.search === 'string' ? to.search : undefined)

    const path = `/${language}${prependWithSlash(providedPath || stripLanguage(window.location.pathname))}`
    const search = !providedPath && !providedSearch ? window.location.search : providedSearch

    return React.createElement(navLink ? NavLink : ReactRouterLink, {
        ...props,
        to: {
            ...(toIsObject ? to : {}),
            pathname: path,
            search,
        },
    })
}

export const Link = branch(props => !props.language, withLanguage)($Link)

export const withLocale = connect(
    createStructuredSelector({
        locale: localeSelector,
    })
)

export const withTranslate = compose(
    injectIntl,
    withProps(({ intl }) => ({
        translate: key => intl.formatMessage({ id: key, defaultMessage: key }),
    }))
)

export const useTranslation = () => {
    const intl = useIntl()
    return key => intl.formatMessage({ id: key, defaultMessage: key })
}

export const T = ({ children, ...props }) => <FormattedMessage id={children} defaultMessage={children} {...props} />

export const withMeta = metaFromProps => WrappedComponent => {
    const component = props => {
        const { title, description, keywords } = typeof metaFromProps === 'function' ? metaFromProps(props) : props

        return (
            <div>
                {title && (
                    <FormattedMessage id={title}>
                        {title => (
                            <Helmet>
                                <title>{title}</title>
                            </Helmet>
                        )}
                    </FormattedMessage>
                )}

                {description && (
                    <FormattedMessage id={description}>
                        {description => (
                            <Helmet>
                                <meta name="description" content={description} />
                            </Helmet>
                        )}
                    </FormattedMessage>
                )}

                {keywords && (
                    <FormattedMessage id={keywords}>
                        {keywords => (
                            <Helmet>
                                <meta
                                    name="keywords"
                                    content={keywords instanceof Array ? keywords.join(', ') : keywords}
                                />
                            </Helmet>
                        )}
                    </FormattedMessage>
                )}

                <WrappedComponent {...props} />
            </div>
        )
    }

    component.displayName = wrapDisplayName(component, 'withMeta')

    return component
}

export const withTranslatedMeta = (title, description, keywords) =>
    compose(
        withMeta(() => ({
            title,
            description,
            keywords,
        }))
    )

export const languageKeyToText = lang => lang?.toLowerCase().replace(/[^a-zA-Z ]/g, '')
